import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { Alert, Badge, Button, Form, Modal, Nav, Pagination, Spinner, Table } from 'react-bootstrap';
import { formatDateTime } from '../utils/dateFormat';
import { useAuth } from '../contexts/AuthContext';
import { hasAnyRole } from '../utils/permissionUtils';
import PackingSlip from './PackingSlip';

const FILTERS = [
  ['new', 'New'], ['topay', 'Waiting for payment'], ['toship', 'To send'], ['shipped', 'Sent'], ['delivered', 'Delivered'], ['returned', 'Returned'], ['cancelled', 'Cancelled'], ['all', 'All']
];
const STATUS = { PENDING: ['New', 'primary'], CONFIRMED: ['Confirmed', 'info'], PROCESSING: ['Being prepared', 'info'], SHIPPED: ['Sent', 'secondary'], DELIVERED: ['Delivered', 'success'], RETURNED: ['Returned', 'dark'], CANCELLED: ['Cancelled', 'danger'] };
const PAYMENT = { PENDING: ['Unpaid', 'warning'], PAID: ['Paid', 'success'], PARTIAL: ['Part paid', 'warning'], REFUNDED: ['Refunded', 'secondary'] };
const METHOD = { UPI: 'UPI', BANK_TRANSFER: 'Bank transfer', COD: 'Cash on delivery', CREDIT: 'On credit terms', ONLINE: 'Paid online (Razorpay)' };

/** The inbox of orders placed on the online store: what is new, waiting for payment, to send and sent, and the steps on each. */
function OnlineOrders() {
  const [filter, setFilter] = useState('new');
  const [search, setSearch] = useState('');
  const [typed, setTyped] = useState('');
  const [page, setPage] = useState(0);
  const [data, setData] = useState(null);
  const [openId, setOpenId] = useState(null);
  const money = useMemo(() => new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }), []);

  const load = useCallback(() => axios.get('/api/store/orders', { params: { filter, q: search || undefined, page } })
    .then((r) => setData(r.data)).catch((e) => toast.error(e.response?.data?.message || 'Could not load orders.')), [filter, search, page]);
  useEffect(() => { load(); }, [load]);

  if (!data) return <div className="py-4 text-center"><Spinner animation="border" size="sm" /></div>;

  return (
    <div className="mt-3" data-testid="online-orders">
      <Nav variant="pills" className="mb-3 flex-wrap gap-1" activeKey={filter} onSelect={(k) => { setFilter(k); setPage(0); }}>
        {FILTERS.map(([key, label]) => (
          <Nav.Item key={key}><Nav.Link eventKey={key} className="py-1">{label} <Badge bg={filter === key ? 'light' : 'secondary'} text={filter === key ? 'dark' : undefined}>{data.counts?.[key] ?? 0}</Badge></Nav.Link></Nav.Item>
        ))}
      </Nav>
      <Form className="mb-3 d-flex gap-2" onSubmit={(e) => { e.preventDefault(); setSearch(typed.trim()); setPage(0); }}>
        <Form.Control type="search" placeholder="Search by order number, name, phone or email" value={typed} onChange={(e) => setTyped(e.target.value)} style={{ maxWidth: 360 }} />
        <Button type="submit" variant="outline-secondary">Search</Button>
      </Form>

      {data.orders.length === 0 ? (
        <Alert variant="light" className="text-center text-muted">{search ? 'No orders match.' : data.counts?.all ? 'Nothing here right now.' : 'No online orders yet. They appear here when a buyer places one.'}</Alert>
      ) : (
        <Table hover responsive size="sm" className="align-middle">
          <thead><tr><th>Order</th><th>Placed</th><th>Buyer</th><th className="text-end">Total</th><th>Payment</th><th>Status</th></tr></thead>
          <tbody>
            {data.orders.map((o) => (
              <tr key={o.id} role="button" onClick={() => setOpenId(o.id)} data-testid={`online-order-${o.orderNumber}`}>
                <td className="fw-semibold">{o.orderNumber}</td>
                <td>{formatDateTime(o.placedAt)}</td>
                <td>{o.businessName || o.buyerName}{o.tradeLink && <Badge bg="info" className="ms-1" title="Ordered with the customer's private trade link">Trade</Badge>}<div className="small text-muted">{o.city}{o.state ? `, ${o.state}` : ''}</div></td>
                <td className="text-end">{money.format(o.total)}</td>
                <td><Badge bg={PAYMENT[o.paymentStatus]?.[1] || 'secondary'} text={PAYMENT[o.paymentStatus]?.[1] === 'warning' ? 'dark' : undefined}>{PAYMENT[o.paymentStatus]?.[0] || o.paymentStatus}</Badge>
                  <div className="small text-muted">{METHOD[o.paymentMethod] || o.paymentMethod}</div>
                  {o.lapsesAt && <div className="small text-danger">Lapses {formatDateTime(o.lapsesAt)}</div>}</td>
                <td><Badge bg={STATUS[o.status]?.[1] || 'secondary'}>{STATUS[o.status]?.[0] || o.status}</Badge>{o.refundFailed && <Badge bg="danger" className="ms-1" title="Razorpay could not complete a refund for this order">Refund failed</Badge>}
                  {o.courierName && <div className="small text-muted">{o.courierName}{o.trackingNumber ? ` ${o.trackingNumber}` : ''}</div>}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
      {data.pages > 1 && (
        <Pagination size="sm" className="justify-content-center">
          <Pagination.Prev disabled={data.page <= 0} onClick={() => setPage(data.page - 1)} />
          <Pagination.Item active>{data.page + 1} of {data.pages}</Pagination.Item>
          <Pagination.Next disabled={data.page >= data.pages - 1} onClick={() => setPage(data.page + 1)} />
        </Pagination>
      )}
      {openId && <OrderDialog id={openId} money={money} onClose={() => setOpenId(null)} onChanged={load} />}
    </div>
  );
}

function OrderDialog({ id, money, onClose, onChanged }) {
  const { user } = useAuth();
  const isManager = hasAnyRole(user, ['ORG_ADMIN', 'SALES_MGR']); // only a manager gives money back
  const [refundToo, setRefundToo] = useState(true);
  const [receiptNote, setReceiptNote] = useState(null); // what happened to the money when the invoice was made
  const [o, setO] = useState(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState(null); // 'paid', 'ship', 'cancel' or 'return' while its small form is open
  const [slipOpen, setSlipOpen] = useState(false); // the printable packing slip
  const [ret, setRet] = useState({ qty: {}, delivery: false, restock: true, reason: '' }); // what a return takes back
  const [fields, setFields] = useState({ reference: '', courier: '', trackingNumber: '', trackingUrl: '', reason: '' });
  const set = (k) => (e) => setFields((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    axios.get(`/api/store/orders/${id}`).then((r) => {
      setO(r.data);
      setFields((f) => ({ ...f, courier: r.data.courierName || '', trackingNumber: r.data.trackingNumber || '', trackingUrl: r.data.trackingUrl || '' }));
    }).catch((e) => { toast.error(e.response?.data?.message || 'Could not open the order.'); onClose(); });
  }, [id, onClose]);

  const act = async (path, body, done) => {
    setBusy(true);
    try {
      const r = await axios.post(`/api/store/orders/${id}/${path}`, body);
      setO(r.data);
      setForm(null);
      toast.success(done);
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.message || 'That could not be done.');
    } finally {
      setBusy(false);
    }
  };

  const makeInvoice = async () => {
    if (!window.confirm('Make the tax invoice for this order? It takes the stock out of the warehouse.')) return;
    setBusy(true);
    try {
      const r = await axios.post(`/api/store/orders/${id}/invoice`);
      setO(r.data);
      setReceiptNote(r.data.receiptNote || null);
      toast.success(`Tax invoice ${r.data.invoiceNumber} made.`);
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.message || 'The invoice could not be made.');
    } finally {
      setBusy(false);
    }
  };

  const sendReturn = async () => {
    const items = Object.entries(ret.qty).map(([productId, q]) => ({ productId: Number(productId), quantity: Number(q) })).filter((i) => i.quantity > 0);
    if (items.length === 0 && !ret.delivery) { toast.warn('Enter how many of each item are coming back.'); return; }
    setBusy(true);
    try {
      const r = await axios.post(`/api/store/orders/${id}/return`, { items, deliveryCharge: ret.delivery, restock: ret.restock, reason: ret.reason });
      setO(r.data);
      setForm(null);
      setRet({ qty: {}, delivery: false, restock: true, reason: '' });
      setReceiptNote(Number(r.data.handBack) > 0 ? `Credit note ${r.data.creditNote} made. Pay the buyer ${money.format(r.data.handBack)} back yourself: it was not paid through Razorpay.` : `Credit note ${r.data.creditNote} made and the money refunded through Razorpay.`);
      toast.success('Return recorded. The buyer has been told.');
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.message || 'The return could not be recorded.');
    } finally {
      setBusy(false);
    }
  };

  if (!o) return null;
  const gst = Number(o.igst) > 0 ? `IGST ${money.format(o.igst)}` : `CGST ${money.format(o.cgst)}, SGST ${money.format(o.sgst)}`;

  return (
    <>
    <Modal show onHide={onClose} size="lg" scrollable data-testid="online-order-dialog">
      <Modal.Header closeButton>
        <Modal.Title>{o.orderNumber} <Badge bg={STATUS[o.status]?.[1] || 'secondary'} className="ms-2 fs-6">{STATUS[o.status]?.[0] || o.status}</Badge></Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="row g-3">
          <div className="col-md-6">
            <div className="text-muted small">Buyer</div>
            <div className="fw-semibold">{o.businessName ? `${o.businessName} (${o.buyerName})` : o.buyerName}</div>
            <div>{o.buyerPhone} · {o.buyerEmail}</div>
            {o.gstin && <div className="small">GSTIN {o.gstin}</div>}
            {o.tradeLink && <div className="small text-info" data-testid="trade-order">Ordered with the customer&apos;s private trade link</div>}
            <div className="text-muted small mt-2">Deliver to</div>
            <div>{o.addressLine}, {o.city}, {o.state} {o.postalCode}</div>
            {o.buyerNote && <div className="mt-2 small"><span className="text-muted">Buyer's note:</span> {o.buyerNote}</div>}
          </div>
          <div className="col-md-6">
            <div className="text-muted small">Payment</div>
            <div>{METHOD[o.paymentMethod] || o.paymentMethod}: <Badge bg={PAYMENT[o.paymentStatus]?.[1] || 'secondary'} text={PAYMENT[o.paymentStatus]?.[1] === 'warning' ? 'dark' : undefined}>{PAYMENT[o.paymentStatus]?.[0] || o.paymentStatus}</Badge></div>
            {o.paidAt && <div className="small text-muted">Paid {formatDateTime(o.paidAt)}{o.paymentReference ? `, ref ${o.paymentReference}` : ''}</div>}
            {o.refundFailure && <Alert variant="danger" className="py-2 small mt-2 mb-1" data-testid="refund-failed">{o.refundFailure}</Alert>}
            {o.refundedAt && <div className="small text-muted" data-testid="refunded-note">Refunded {formatDateTime(o.refundedAt)}{o.refundId ? `, Razorpay refund ${o.refundId}` : ''}</div>}
            {o.lapsesAt && <div className="small text-danger" data-testid="lapses-at">Cancelled automatically at {formatDateTime(o.lapsesAt)} if still unpaid</div>}
            <div className="text-muted small mt-2">Sending</div>
            {o.courierName ? <div>{o.courierName}{o.trackingNumber ? `, ${o.trackingNumber}` : ''}{o.trackingUrl && <> <a href={o.trackingUrl} target="_blank" rel="noreferrer">track</a></>}</div> : <div className="text-muted">Not sent yet</div>}
            {o.cancelReason && <div className="mt-2 small"><span className="text-muted">Cancelled:</span> {o.cancelReason}</div>}
          </div>
        </div>

        <Table size="sm" className="mt-3 mb-1">
          <tbody>
            {o.lines.map((l, i) => <tr key={`${l.name}-${i}`}><td>{l.name}{l.sku && <span className="text-muted small"> {l.sku}</span>}{Number(l.returned) > 0 && <span className="text-danger small"> ({Number(l.returned)} returned)</span>}</td><td className="text-end">&times; {l.quantity}</td><td className="text-end">{money.format(l.lineTotal)}</td></tr>)}
            <tr><td colSpan={2} className="text-muted small">GST {gst}</td><td /></tr>
            {Number(o.shipping) > 0 && <tr><td colSpan={2}>Shipping</td><td className="text-end">{money.format(o.shipping)}</td></tr>}
            <tr className="fw-bold"><td colSpan={2}>Total</td><td className="text-end">{money.format(o.total)}</td></tr>
          </tbody>
        </Table>

        {o.invoiced
          ? (
            <div className="small text-muted" data-testid="invoice-note">
              Tax invoice <Link to={`/sales/salebills?find=${encodeURIComponent(o.invoiceNumber || '')}`} onClick={onClose}>{o.invoiceNumber || 'made'}</Link>: the stock has gone out.
              {o.receiptRecorded && <> The money received is recorded against it.</>} Once invoiced the order can't be cancelled: take items back with <strong>Return items</strong>.
            </div>
          )
          : o.status !== 'CANCELLED' && <div className="small text-muted">Stock is held for this order. <strong>Make tax invoice</strong> (below) takes the stock out, picking the lots for you, and records the payment if it is already in.</div>}
        {o.returns && o.returns.length > 0 && (
          <div className="small mt-2" data-testid="returns-list">
            <div className="text-muted">Returns</div>
            {o.returns.map((r) => (
              <div key={r.noteNumber}>
                {formatDateTime(r.at)}: {r.summary} &middot; credit note {r.noteNumber} for {money.format(r.amount)}
                {Number(r.refundedAmount) > 0 ? ', refunded through Razorpay' : Number(r.handBack) > 0 ? ', to be paid back by hand' : ''}{r.restocked ? ', back in stock' : ''}
              </div>
            ))}
          </div>
        )}
        {receiptNote && <Alert variant="info" className="py-2 small mt-2" data-testid="receipt-note">{receiptNote}</Alert>}
        {o.deliveredAt && <div className="small text-muted mt-1" data-testid="delivered-note">Delivered {formatDateTime(o.deliveredAt)}.</div>}

        {form === 'paid' && (
          <Form className="mt-3 border rounded p-3" onSubmit={(e) => { e.preventDefault(); act('paid', { reference: fields.reference }, 'Marked paid.'); }}>
            <Form.Group><Form.Label>Payment reference (UTR or receipt number, optional)</Form.Label><Form.Control value={fields.reference} onChange={set('reference')} maxLength={100} /></Form.Group>
            <div className="mt-2"><Button type="submit" size="sm" disabled={busy}>Mark paid</Button> <Button size="sm" variant="link" onClick={() => setForm(null)}>Back</Button></div>
          </Form>
        )}
        {form === 'ship' && (
          <Form className="mt-3 border rounded p-3" onSubmit={(e) => { e.preventDefault(); act('ship', { courier: fields.courier, trackingNumber: fields.trackingNumber, trackingUrl: fields.trackingUrl }, 'Saved. The buyer has been told.'); }}>
            <div className="row g-2">
              <div className="col-md-4"><Form.Label>Courier or transport</Form.Label><Form.Control value={fields.courier} onChange={set('courier')} maxLength={80} required /></div>
              <div className="col-md-4"><Form.Label>Tracking number</Form.Label><Form.Control value={fields.trackingNumber} onChange={set('trackingNumber')} maxLength={80} /></div>
              <div className="col-md-4"><Form.Label>Tracking link</Form.Label><Form.Control value={fields.trackingUrl} onChange={set('trackingUrl')} maxLength={300} placeholder="https://" /></div>
            </div>
            <div className="mt-2"><Button type="submit" size="sm" disabled={busy}>{o.status === 'SHIPPED' || o.status === 'DELIVERED' ? 'Save' : 'Mark as sent'}</Button> <Button size="sm" variant="link" onClick={() => setForm(null)}>Back</Button></div>
          </Form>
        )}
        {form === 'return' && (
          <Form className="mt-3 border rounded p-3" data-testid="return-form" onSubmit={(e) => { e.preventDefault(); sendReturn(); }}>
            <div className="fw-semibold mb-2">Return items</div>
            <Table size="sm" className="mb-2">
              <tbody>
                {o.lines.filter((l) => Number(l.returnable) > 0).map((l) => (
                  <tr key={l.productId}>
                    <td>{l.name}</td>
                    <td className="text-muted small">up to {Number(l.returnable)}</td>
                    <td style={{ width: 110 }}><Form.Control size="sm" type="number" min="0" max={Number(l.returnable)} step="1" placeholder="0" value={ret.qty[l.productId] || ''}
                      data-testid={`return-qty-${l.productId}`} onChange={(e) => setRet((r) => ({ ...r, qty: { ...r.qty, [l.productId]: e.target.value } }))} /></td>
                  </tr>
                ))}
              </tbody>
            </Table>
            {o.deliveryReturnable && <Form.Check type="checkbox" id="return-delivery" checked={ret.delivery} onChange={(e) => setRet((r) => ({ ...r, delivery: e.target.checked }))} label={`Also give back the delivery charge (${money.format(o.shipping)})`} />}
            <Form.Check type="checkbox" id="return-restock" checked={ret.restock} onChange={(e) => setRet((r) => ({ ...r, restock: e.target.checked }))} label="Put the returned goods back into stock (untick if damaged)" />
            <Form.Group className="mt-2"><Form.Label>Reason (the credit note keeps it)</Form.Label><Form.Control value={ret.reason} maxLength={300} onChange={(e) => setRet((r) => ({ ...r, reason: e.target.value }))} /></Form.Group>
            <div className="small text-muted mt-2">A credit note is made against the tax invoice.{o.paymentStatus === 'PAID' ? (o.online ? ' The money goes back to the buyer through Razorpay.' : ' You pay the buyer back yourself; the books already show it.') : ''}</div>
            <div className="mt-2"><Button type="submit" size="sm" variant="danger" disabled={busy} data-testid="send-return">Record the return</Button> <Button size="sm" variant="link" onClick={() => setForm(null)}>Back</Button></div>
          </Form>
        )}
        {form === 'cancel' && (
          <Form className="mt-3 border rounded p-3" onSubmit={(e) => {
            e.preventDefault();
            if (o.canCancelAndRefund && isManager && refundToo) act('cancel-and-refund', { reason: fields.reason }, 'Order cancelled and the payment refunded through Razorpay. The buyer has been told.');
            else act('cancel', { reason: fields.reason }, 'Order cancelled. The stock is free again.');
          }}>
            <Form.Group><Form.Label>Reason (the buyer is told)</Form.Label><Form.Control value={fields.reason} onChange={set('reason')} maxLength={300} /></Form.Group>
            {o.canCancelAndRefund && isManager && (
              <Form.Check className="mt-2" type="checkbox" id="refund-too" checked={refundToo} onChange={(e) => setRefundToo(e.target.checked)} data-testid="refund-too"
                label={`Also refund ${money.format(o.total)} to the buyer now, through Razorpay`} />
            )}
            {o.canCancelAndRefund && !isManager && <div className="small text-danger mt-1">This order was paid online. Cancel it here, then ask a manager to refund it.</div>}
            {o.paymentStatus === 'PAID' && !o.canCancelAndRefund && <div className="small text-danger mt-1">This order was paid: refund the buyer yourself, then mark it refunded.</div>}
            <div className="mt-2"><Button type="submit" size="sm" variant="danger" disabled={busy}>{o.canCancelAndRefund && isManager && refundToo ? 'Cancel and refund' : 'Confirm cancellation'}</Button> <Button size="sm" variant="link" onClick={() => setForm(null)}>Keep the order</Button></div>
          </Form>
        )}
      </Modal.Body>
      <Modal.Footer className="justify-content-start flex-wrap gap-2">
        {o.status !== 'CANCELLED' && <Button size="sm" variant="outline-secondary" disabled={busy} data-testid="open-packing-slip" onClick={() => setSlipOpen(true)}><i className="bi bi-printer me-1" />Packing slip</Button>}
        {o.canConfirm && <Button size="sm" disabled={busy} onClick={() => act('confirm', null, 'Confirmed. The buyer has been told.')}>Confirm</Button>}
        {o.canInvoice && <Button size="sm" disabled={busy} onClick={makeInvoice} data-testid="make-invoice">Make tax invoice</Button>}
        {o.canMarkPaid && <Button size="sm" variant="outline-primary" disabled={busy} onClick={() => setForm('paid')}>Mark paid</Button>}
        {o.canShip && <Button size="sm" variant="outline-primary" disabled={busy} onClick={() => setForm('ship')}>{o.courierName ? 'Edit courier details' : 'Mark as sent'}</Button>}
        {o.canDeliver && <Button size="sm" variant="outline-success" disabled={busy} data-testid="mark-delivered" onClick={() => act('delivered', null, 'Marked delivered. The buyer has been told.')}>Mark delivered</Button>}
        {o.canRefundOnline && isManager && (
          <Button size="sm" variant="danger" disabled={busy} data-testid="refund-online" onClick={() => {
            if (window.confirm(`Refund ${money.format(o.total)} to the buyer through Razorpay? This cannot be undone.`)) act('refund', null, 'Refunded through Razorpay. The buyer has been told.');
          }}>Refund {money.format(o.total)} through Razorpay</Button>
        )}
        {o.canRefundOnline && !isManager && <span className="small text-muted align-self-center">Paid online: a manager can refund it through Razorpay.</span>}
        {o.canRefund && <Button size="sm" variant="outline-secondary" disabled={busy} onClick={() => act('refunded', null, 'Marked refunded.')}>Mark refunded</Button>}
        {o.canReturn && isManager && <Button size="sm" variant="outline-danger" disabled={busy} data-testid="return-items" onClick={() => setForm('return')}>Return items</Button>}
        {o.canReturn && !isManager && <span className="small text-muted align-self-center">A manager takes items back.</span>}
        {o.canCancel && form !== 'cancel' && <Button size="sm" variant="outline-danger" className="ms-auto" disabled={busy} onClick={() => setForm('cancel')}>Cancel order</Button>}
      </Modal.Footer>
    </Modal>
    {slipOpen && <PackingSlip orderId={id} onClose={() => setSlipOpen(false)} />}
    </>
  );
}

export default OnlineOrders;
